import React, { useState, useEffect, useCallback } from 'react';
import './aside.css';
import Button from '../Button/Button';
import Floor from '../Floor/floor';
import getEmploeeys from './getEmploeeys';
import Modal from '../../../modal/modal';
import ModalContent from '../../../modal/ModalContent';
import { Link, useParams } from 'react-router-dom';
import ModalExit from '../../modalContent/exit/exit';
import ModalAddEmployee from '../../modalContent/addEmployee/addEmployee';
import axiosInstance from '../../../utils/router/axiosInstance';
import { logger } from '../../../utils/logger';
import { useAuth } from '../../../context/AuthContext';
// WEB-17: непрочитанное берётся ГОТОВЫМ из каркаса. Свой вызов useUnread здесь
// удвоил бы опрос сервера — см. докстринг unreadContext.
import { useUnreadValue } from '../../../msg/unreadContext';
// WEB-25: тумблер уведомлений. В шапке он скрыт на узком экране (правило из
// WEB-10), и владелец не мог включить уведомления с телефона вовсе.
import PushToggle from '../../../msg/pushToggle';

// WEB-7: создание карточки из aside + должность в списке — за build-флагом
// (дефолт off = список и футер байт-в-байт)
const EMPLOYEE_CARD_ENABLED = process.env.REACT_APP_EMPLOYEE_CARD === 'true';

// WEB-9: open/onClose нужны только выдвижной панели на узком экране; каркас
// (CabinetShell) владеет флагом и даёт его сюда. Значения по умолчанию
// оставляют поведение прежним для вызовов без этих параметров.
// panelRef — тому же каркасу: открыв панель, он обязан увести в неё фокус и
// удержать его там, пока она открыта (ревью Codex WEB-9, High).
const Aside = ({ floor = true, open = false, onClose, panelRef }) => {
  // WEB-2: не-админа «Опции» ведут в профиль (админ-вкладки ему скрыты/гейтятся)
  // MSG-7: messengerV2 показывает ссылку «Чат» у сотрудника (флаг off — как раньше)
  const { isAdmin, messengerV2, msgInvites, msgRooms } = useAuth();
  // WEB-17: раздел «Чат» в меню. До него попасть в переписку можно было только
  // значком в шапке — а он есть ТОЛЬКО пока есть непрочитанное — или тремя
  // нажатиями через «Опции» → «Мой QR». Прочитал всё — и переписка исчезала из
  // интерфейса совсем.
  const unread = useUnreadValue();
  const [employees, setEmployees] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [isModalActive, setIsModalActive] = useState(false);
  const [isAddEmployeeActive, setIsAddEmployeeActive] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const { id } = useParams();
  // WEB-7: гард alert'ов модала создания после ухода со страницы (aside
  // размонтируется вместе со страницей); взводится в эффекте — StrictMode-safe
  const aliveRef = React.useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const response = await axiosInstance.get('website/buildings');
      const data = response.data;
      setBuildings(data);
      const buildingId = id || localStorage.getItem('selectedBuildingId') || data[0]?.id;
      const building = data.find((b) => b.id === parseInt(buildingId));
      if (building) {
        setSelectedBuilding(building);
        localStorage.setItem('selectedBuildingId', buildingId);
        localStorage.setItem('current_floor_amount', building.floor_amount);
      }
    } catch (error) {
      logger.error('Error fetching data:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // WEB-9: переход по ссылке закрывает выдвижное меню. Иначе на телефоне
  // панель остаётся висеть поверх страницы, на которую человек только что
  // ушёл, и её приходится закрывать вторым жестом.
  const closeMenu = () => onClose && onClose();

  /**
   * Сколько ждёт — СЛОВАМИ. Значок рисует цифру, но «2», зачитанная голосом
   * посреди названия комнаты, не значит ничего; подпись ссылки говорит целиком.
   */
  const waitingLabel = (n) => (n > 99
    ? '99+ новых'
    : `${n} ${(n % 10 === 1 && n % 100 !== 11) ? 'новое' : 'новых'}`);

  /**
   * WEB-17: раздел «Чат» — общая комната и люди, вошедшие по приглашению.
   *
   * 🔴 Сотрудников здесь НЕТ намеренно: у них своя ссылка «Чат» прямо в строке
   * списка ниже, и повтор показал бы одного человека в меню дважды. Счётчика по
   * сотрудникам сервер и не считает (докстринг useUnread, «чего НЕ покрывает»).
   *
   * 🔴 Комната показывается ДАЖЕ когда её ещё нет. Она заводится при первом
   * открытии владельцем, а до того участник, пришедший по ссылке, ловит 404 —
   * так и случилось на прогоне 25.08. Пункт меню — самый короткий путь её
   * завести, и он на виду.
   */
  const chatSection = () => {
    if (!unread.enabled) return null;
    // 🔴 Участники — только пока включён СВОЙ флаг (ревью Codex WEB-17, Low).
    // `unread.members` держит последний удачный ответ, и хук, переставший
    // опрашивать `/msg/members` после выключения флага, его НЕ чистит: раздел
    // показывал бы ссылки в переписку, поверхности которой на сервере уже нет.
    const people = (msgInvites ? unread.members : []).map((m) => ({
      key: `member-${m.id}`,
      to: `/msg/m/${m.id}`,
      name: m.display_name,
      waiting: m.unread ?? 0,
    }));
    const items = msgRooms
      ? [{
        key: 'room', to: '/msg/room', name: 'Общая комната',
        waiting: unread.room?.unread ?? 0,
      }, ...people]
      : people;
    return (
      <>
        <div className="asideSection asideSectionChat" data-testid="aside-chat">
          <p className="asideSectionTitle">Чат</p>
          <ul className="chat-list">
            {items.map((item) => (
              <li key={item.key}>
                <Link
                  to={item.to}
                  className="nav-link asideChatLink"
                  onClick={closeMenu}
                  aria-label={item.waiting > 0
                    ? `${item.name}, ${waitingLabel(item.waiting)}`
                    : item.name}
                >
                  <span className="asideChatName">{item.name}</span>
                  {item.waiting > 0 && (
                    <span className="asideBadge" aria-hidden="true">
                      {item.waiting > 99 ? '99+' : item.waiting}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
          {msgInvites && (
            <Link
              to="/msg/invite"
              className="nav-link asideChatInvite"
              onClick={closeMenu}
            >
              Пригласить в чат
            </Link>
          )}
        </div>
        <hr className="asideDivider" />
      </>
    );
  };

  const handleBuildingSelect = (buildingId) => {
    const selected = buildings.find((building) => building.id === buildingId);
    if (selected) {
      setSelectedBuilding(selected);
      localStorage.setItem('selectedBuildingId', buildingId);
      localStorage.setItem('current_floor_amount', selected.floor_amount);
    }
  };

  const fetchAndUpdateEmployees = useCallback(async () => {
    const updatedEmployeesData = await getEmploeeys();
    updatedEmployeesData.sort((a, b) => (a.status === b.status ? 0 : a.status ? -1 : 1));
    setEmployees(updatedEmployeesData);
  }, []);

  useEffect(() => {
    fetchAndUpdateEmployees();
    const intervalId = setInterval(fetchAndUpdateEmployees, 20000);
    return () => clearInterval(intervalId);
  }, [fetchAndUpdateEmployees]);

  return (
    <div className="aside" ref={panelRef}>
      <div className="asideHead">
        <div className="asideBrand">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--ui-accent)" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
            <path d="M4.5 9.5a10.5 10.5 0 0 1 15 0" />
            <path d="M7.5 13a6.4 6.4 0 0 1 9 0" />
            <path d="M10.5 16.5a2.2 2.2 0 0 1 3 0" />
            <circle cx="12" cy="19.5" r="1" fill="var(--ui-accent)" stroke="none" />
          </svg>
          <span className="asideBrandName">wificontrol</span>
        </div>
        <button
          type="button"
          className="asideClose"
          aria-label="Закрыть меню"
          tabIndex={open ? 0 : -1}
          onClick={() => onClose && onClose()}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
            <path d="m7 7 10 10" />
            <path d="m17 7-10 10" />
          </svg>
        </button>
      </div>
      <div className="asideContent">
        <div className="asideSection">
          <p className="asideSectionTitle">Помещения</p>
          <ul className="building-list">
            {buildings.map((building) => (
              <li key={building.id}>
                <Link
                  to={`/map/${building.id}`}
                  className={`nav-link map ${id === building.id.toString() ? 'active' : ''}`}
                  onClick={() => {
                    handleBuildingSelect(building.id);
                    closeMenu();
                  }}
                >
                  {building.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <hr className="asideDivider"></hr>
        {chatSection()}
        {/* WEB-25: уведомления — настройка УСТРОЙСТВА, а не отдельного чата,
            поэтому строка стоит рядом с перепиской, но НЕ внутри её раздела.
            🔴 Условие своё (ревью 09.09): раздел «Чат» живёт под
            `msgInvites || msgRooms`, а переписка с сотрудниками — под
            `messengerV2`, и push для неё сервер шлёт. Наследуй строка чужое
            условие — при включённом только `messengerV2` человек получал бы
            уведомления, но включить их ему было бы негде.
            Меню при нажатии не закрывается: человек должен увидеть, что
            состояние сменилось. */}
        {(messengerV2 || unread.enabled) && (
          <div className="asideSection asideSectionPush" data-testid="aside-push-section">
            <PushToggle variant="menu" testId="aside-push" />
          </div>
        )}
        <div className="asideSection asideSectionEmployees">
          <p className="asideSectionTitle">Сотрудники</p>
          <ul className="employee-list">
            {employees.map((employee) => (
              <li key={employee.id} className={employee.status ? 'green-marker' : 'red-marker'}>
                <Link to={`/employee/${employee.user_id}`} onClick={closeMenu}>
                  {employee.fio}
                  {EMPLOYEE_CARD_ENABLED && employee.position ? ` — ${employee.position}` : ''}
                </Link>
                {messengerV2 && (
                  <Link className="employeeChatLink" to={`/msg/${employee.user_id}`} title="Защищённый чат" onClick={closeMenu}>
                    Чат
                  </Link>
                )}
              </li>
            ))}
          </ul>
          {EMPLOYEE_CARD_ENABLED && isAdmin && (
            <Button
              className="addEmployeeButton"
              text="Добавить сотрудника"
              onClick={() => setIsAddEmployeeActive(true)}
            />
          )}
        </div>
      </div>
      <div className="asideFooter">
        {floor && selectedBuilding && selectedBuilding.floor_amount && (
          <div className="floor">
            <Floor floorAmount={selectedBuilding.floor_amount} />
          </div>
        )}
        <div className="asideActions">
          <Link
            to={isAdmin ? '/settings/wifi' : '/settings/profile'}
            className={`nav-link asideAction asideSettings ${id === 'settings' ? 'active' : ''}`}
            onClick={closeMenu}
          >
            {/* WEB-17: было «Опции». Слово не называло ничего из того, что за
                ним лежит — Wi-Fi точки, код регистрации, приглашения, профиль,
                оформление, — и человек искал их где угодно, только не здесь. */}
            Настройки
          </Link>
          <Button className={'exitButton asideAction'} text="Выход" onClick={() => setIsModalActive(true)} />
        </div>
      </div>
      <Modal active={isModalActive} setActive={setIsModalActive} width={'600px'} height={'180px'}>
        <ModalContent>
          <ModalExit setIsModalActive={setIsModalActive} />
        </ModalContent>
      </Modal>
      {EMPLOYEE_CARD_ENABLED && (
        <Modal
          active={isAddEmployeeActive}
          setActive={setIsAddEmployeeActive}
          width={'420px'}
          height={'640px'}
        >
          <ModalContent>
            {/* условный МАУНТ (ревью-воркфлоу, Medium): Modal рендерит детей
                всегда — без условия форма жила бы один раз на всю сессию и
                показывала данные предыдущего сотрудника при повторном открытии */}
            {isAddEmployeeActive && (
              <ModalAddEmployee
                onCreated={() => {
                  setIsAddEmployeeActive(false);
                  fetchAndUpdateEmployees(); // новый сотрудник сразу в списке
                }}
                isPageAlive={() => aliveRef.current}
              />
            )}
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default Aside;
